<template>
    <div class="documents-container">
        <div class="page-header">
            <h1 class="page-title">📁 Documents</h1>
        </div>

        <div class="documents-header">
            <div class="breadcrumb">
                <span v-for="(crumb, index) in breadcrumbs" :key="index">
                    <span v-if="index > 0"> / </span>
                    <a href="#" @click.prevent="navigateToFolder(crumb.path)" 
                       :class="{ 'current': index === breadcrumbs.length - 1 }">
                        {{ crumb.name }}
                    </a>
                </span>
            </div>

            <div class="toolbar">
                <button class="btn btn-primary" @click="showUploadModal = true">
                    📤 Upload File
                </button>
                <button class="btn btn-secondary" @click="showCreateFolderModal = true">
                    📁 New Folder
                </button>
            </div>
        </div>

        <div class="documents-grid">
            <!-- Back to parent folder -->
            <div v-if="currentPath !== '/'" class="document-item folder back-button" @click="goBack()">
                <div class="document-icon">
                    ↩️
                </div>
                <div class="document-name">
                    <span>← Back to Parent</span>
                </div>
            </div>

            <!-- Folders -->
            <div v-for="folder in folders" :key="folder.id" 
                 class="document-item folder"
                 @click="openFolder(folder)">
                <div class="document-icon">
                    📁
                </div>
                <div class="document-name">
                    <span>{{ folder.name }}</span>
                    <small>{{ formatDate(folder.created_at) }}</small>
                </div>
            </div>

            <!-- Files -->
            <div v-for="file in files" :key="file.id" 
                 class="document-item file"
                 :class="{ 'selected': selectedItem && selectedItem.id === file.id && selectedItem.type === 'file' }"
                 @click="selectItem(file, 'file')">
                <div class="document-icon">
                    {{ getFileIcon(file.type) }}
                </div>
                <div class="document-name">
                    <span>{{ file.name }}</span>
                    <small>{{ formatSize(file.size) }} • {{ formatDate(file.created_at) }}</small>
                </div>
            </div>
        </div>

        <!-- Selected File Actions -->
        <div v-if="selectedItem && selectedItem.type === 'file'" class="action-bar">
            <div class="action-bar-content">
                <div class="selected-item-info">
                    <div class="selected-icon">
                        {{ getFileIcon(selectedItem.data.type) }}
                    </div>
                    <div class="selected-details">
                        <h4>{{ selectedItem.data.name }}</h4>
                        <p>File • {{ formatSize(selectedItem.data.size) }} • {{ formatDate(selectedItem.data.created_at) }}</p>
                    </div>
                </div>
                <div class="action-buttons">
<!--                    <button @click="openFile(selectedItem.data)" class="action-button primary">-->
<!--                        <span class="action-icon">👁️</span>-->
<!--                        <span>View</span>-->
<!--                    </button>-->
                    <button @click="downloadFile(selectedItem.data)" class="action-button secondary">
                        <span class="action-icon">⬇️</span>
                        <span>Download</span>
                    </button>
                    <button @click="showPermissions(selectedItem.data)" class="action-button">
                        <span class="action-icon">🔒</span>
                        <span>Permissions</span>
                    </button>
                    <button @click="renameItem(selectedItem.data)" class="action-button">
                        <span class="action-icon">✏️</span>
                        <span>Rename</span>
                    </button>
                    <button @click="deleteItem(selectedItem.data)" class="action-button danger">
                        <span class="action-icon">🗑️</span>
                        <span>Delete</span>
                    </button>
                </div>
                <button @click="clearSelection" class="close-action-bar">✕</button>
            </div>
        </div>

        <!-- Upload Modal -->
        <div v-if="showUploadModal" class="modal-overlay" @click="showUploadModal = false">
            <div class="modal" @click.stop>
                <div class="modal-header">
                    <h3>Upload Files</h3>
                    <button @click="showUploadModal = false" class="close-btn">✕</button>
                </div>
                <div class="modal-body">
                    <div class="upload-zone" 
                         @drop.prevent="handleDrop" 
                         @dragover.prevent
                         @dragenter.prevent>
                        <input type="file" 
                               ref="fileInput" 
                               @change="handleFileSelect" 
                               multiple 
                               style="display: none">
                        <p>Drop files here or <a href="#" @click.prevent="$refs.fileInput.click()">browse</a></p>
                    </div>
                    <div v-if="uploadFiles.length" class="upload-list">
                        <div v-for="(file, index) in uploadFiles" :key="index" class="upload-item">
                            <span>{{ file.name }}</span>
                            <button @click="removeUploadFile(index)">✕</button>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button @click="uploadSelectedFiles" class="btn btn-primary" :disabled="!uploadFiles.length">
                        Upload
                    </button>
                    <button @click="showUploadModal = false" class="btn btn-secondary">
                        Cancel
                    </button>
                </div>
            </div>
        </div>

        <!-- Create Folder Modal -->
        <div v-if="showCreateFolderModal" class="modal-overlay" @click="showCreateFolderModal = false">
            <div class="modal" @click.stop>
                <div class="modal-header">
                    <h3>Create New Folder</h3>
                    <button @click="showCreateFolderModal = false" class="close-btn">✕</button>
                </div>
                <div class="modal-body">
                    <input v-model="newFolderName" 
                           type="text" 
                           placeholder="Folder name" 
                           class="form-control"
                           @keyup.enter="createFolder">
                </div>
                <div class="modal-footer">
                    <button @click="createFolder" class="btn btn-primary" :disabled="!newFolderName">
                        Create
                    </button>
                    <button @click="showCreateFolderModal = false" class="btn btn-secondary">
                        Cancel
                    </button>
                </div>
            </div>
        </div>

        <!-- Permissions Modal -->
        <div v-if="showPermissionsModal" class="modal-overlay" @click="showPermissionsModal = false">
            <div class="modal large-modal" @click.stop>
                <div class="modal-header">
                    <h3>Manage Permissions - {{ selectedPermissionsItem?.name }}</h3>
                    <button @click="showPermissionsModal = false" class="close-btn">✕</button>
                </div>
                <div class="modal-body">
                    <div class="permissions-section">
                        <div class="form-group">
                            <label class="checkbox-label">
                                <input type="checkbox" v-model="isPublic" @change="togglePublic">
                                <span class="checkmark">✓</span>
                                Make this {{ selectedPermissionsItem?.type ? 'file' : 'folder' }} public (accessible to all users)
                            </label>
                        </div>

                        <div v-if="!isPublic" class="users-section">
                            <h4>Grant Access to Specific Users</h4>

                            <div class="user-search">
                                <select v-model="selectedUserId" class="form-control">
                                    <option value="">Select a user to add...</option>
                                    <option v-for="user in availableUsers" :key="user.id" :value="user.id">
                                        {{ user.name }} ({{ user.email }})
                                    </option>
                                </select>
                                <button @click="addUserPermission" class="btn btn-primary" :disabled="!selectedUserId">
                                    Add User
                                </button>
                            </div>

                            <div class="current-users">
                                <h5>Users with Access:</h5>
                                <div v-if="allowedUsers.length === 0" class="no-users">
                                    No specific users have been granted access
                                </div>
                                <div v-else class="user-list">
                                    <div v-for="user in allowedUsers" :key="user.id" class="user-item">
                                        <div class="user-info">
                                            <span class="user-name">{{ user.name }}</span>
                                            <span class="user-email">{{ user.email }}</span>
                                        </div>
                                        <button @click="removeUserPermission(user.id)" class="btn btn-danger btn-sm">
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button @click="savePermissions" class="btn btn-primary">
                        Save Changes
                    </button>
                    <button @click="showPermissionsModal = false" class="btn btn-secondary">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DocumentsIndex',
    data() {
        return {
            currentPath: '/',
            folders: [],
            files: [],
            breadcrumbs: [{ name: 'Documents', path: '/' }],
            showUploadModal: false,
            showCreateFolderModal: false,
            showPermissionsModal: false,
            uploadFiles: [],
            newFolderName: '',
            loading: false,
            selectedItem: null,
            selectedPermissionsItem: null,
            isPublic: false,
            selectedUserId: '',
            allUsers: [],
            allowedUsers: []
        };
    },
    mounted() {
        this.loadDocuments();
    },
    methods: {
        async loadDocuments() {
            this.loading = true;
            try {
                const response = await this.$api.get(`/documents`, {
                    params: { path: this.currentPath }
                });
                this.folders = response.data.folders;
                this.files = response.data.files;
            } catch (error) {
                console.error('Error loading documents:', error);
                this.$toast.error('Error loading documents');
            } finally {
                this.loading = false;
            }
        },

        openFolder(folder) {
            this.currentPath = this.currentPath === '/' ? `/${folder.name}` : `${this.currentPath}/${folder.name}`;
            this.breadcrumbs.push({ name: folder.name, path: this.currentPath });
            this.loadDocuments();
        },

        navigateToFolder(path) {
            this.currentPath = path;
            this.breadcrumbs = this.breadcrumbs.slice(0, this.breadcrumbs.findIndex(b => b.path === path) + 1);
            this.loadDocuments();
        },

        goBack() {
            if (this.breadcrumbs.length > 1) {
                this.breadcrumbs.pop();
                this.currentPath = this.breadcrumbs[this.breadcrumbs.length - 1].path;
                this.loadDocuments();
            }
        },

        getFileIcon(type) {
            const iconMap = {
                'pdf': '📄',
                'doc': '📝',
                'docx': '📝',
                'xls': '📊',
                'xlsx': '📊',
                'ppt': '📊',
                'pptx': '📊',
                'txt': '📄',
                'jpg': '🖼️',
                'jpeg': '🖼️',
                'png': '🖼️',
                'gif': '🖼️',
                'zip': '🗜️',
                'rar': '🗜️'
            };
            return iconMap[type] || '📄';
        },

        handleDrop(event) {
            const files = Array.from(event.dataTransfer.files);
            this.uploadFiles.push(...files);
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            this.uploadFiles.push(...files);
        },

        removeUploadFile(index) {
            this.uploadFiles.splice(index, 1);
        },

        async uploadSelectedFiles() {
            if (!this.uploadFiles.length) return;

            const formData = new FormData();
            this.uploadFiles.forEach(file => {
                formData.append('files[]', file);
            });
            formData.append('path', this.currentPath);

            try {
                await this.$api.post('/documents/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                this.$toast.success('Files uploaded successfully');
                this.showUploadModal = false;
                this.uploadFiles = [];
                this.loadDocuments();
            } catch (error) {
                console.error('Upload error:', error);
                this.$toast.error('Error uploading files');
            }
        },

        async createFolder() {
            if (!this.newFolderName) return;

            try {
                await this.$api.post('/documents/folder', {
                    name: this.newFolderName,
                    path: this.currentPath
                });
                this.$toast.success('Folder created successfully');
                this.showCreateFolderModal = false;
                this.newFolderName = '';
                this.loadDocuments();
            } catch (error) {
                console.error('Create folder error:', error);
                this.$toast.error('Error creating folder');
            }
        },

        openFile(file) {
            window.open(`http://127.0.0.1:8001/api/documents/view/${file.id}`, '_blank');
        },

        async downloadFile(file) {
            try {
                const response = await this.$api.get(`/documents/download/${file.id}`, {
                    responseType: 'blob'
                });

                // Create blob URL and download
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.$toast.success('File downloaded successfully');
            } catch (error) {
                console.error('Download error:', error);
                this.$toast.error('Error downloading file');
            }
        },

        async renameItem(item) {
            const newName = prompt(`Rename ${item.name}:`, item.name);
            if (newName && newName !== item.name) {
                try {
                    await this.$api.put(`/documents/${item.id}/rename`, {
                        name: newName
                    });
                    this.$toast.success('Renamed successfully');
                    this.loadDocuments();
                } catch (error) {
                    console.error('Rename error:', error);
                    this.$toast.error('Error renaming item');
                }
            }
        },

        async deleteItem(item) {
            if (confirm(`Are you sure you want to delete ${item.name}?`)) {
                try {
                    await this.$api.delete(`/documents/${item.id}`);
                    this.$toast.success('Deleted successfully');
                    this.loadDocuments();
                } catch (error) {
                    console.error('Delete error:', error);
                    this.$toast.error('Error deleting item');
                }
            }
        },

        formatSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        formatDate(date) {
            return new Date(date).toLocaleDateString();
        },

        async showPermissions(item) {
            this.selectedPermissionsItem = item;
            this.isPublic = item.is_public || false;
            this.showPermissionsModal = true;

            // Load all users
            try {
                const response = await this.$api.get('/documents-users');
                this.allUsers = response.data;
            } catch (error) {
                console.error('Error loading users:', error);
                this.$toast.error('Error loading users');
            }

            // Load current allowed users
            this.loadAllowedUsers();
        },

        async loadAllowedUsers() {
            if (this.selectedPermissionsItem && this.selectedPermissionsItem.permissions) {
                this.allowedUsers = this.allUsers.filter(user => 
                    this.selectedPermissionsItem.permissions.includes(user.id)
                );
            } else {
                this.allowedUsers = [];
            }
        },

        async togglePublic() {
            // If making public, clear specific user permissions
            if (this.isPublic) {
                this.allowedUsers = [];
            }
        },

        async addUserPermission() {
            if (!this.selectedUserId) return;

            const user = this.allUsers.find(u => u.id == this.selectedUserId);
            if (user && !this.allowedUsers.find(u => u.id == user.id)) {
                this.allowedUsers.push(user);
                this.selectedUserId = '';
            }
        },

        removeUserPermission(userId) {
            this.allowedUsers = this.allowedUsers.filter(user => user.id !== userId);
        },

        async savePermissions() {
            try {
                const permissions = this.allowedUsers.map(user => user.id);

                await this.$api.put(`/documents/${this.selectedPermissionsItem.id}/permissions`, {
                    permissions: permissions,
                    is_public: this.isPublic
                });

                this.$toast.success('Permissions updated successfully');
                this.showPermissionsModal = false;
                this.loadDocuments();
            } catch (error) {
                console.error('Error updating permissions:', error);
                this.$toast.error('Error updating permissions');
            }
        },

        selectItem(item, type) {
            if (type === 'file') {
                this.selectedItem = {
                    data: item,
                    type: type,
                    id: item.id
                };
            }
        },

        clearSelection() {
            this.selectedItem = null;
        }
    },

    computed: {
        availableUsers() {
            return this.allUsers.filter(user => 
                !this.allowedUsers.find(allowed => allowed.id === user.id)
            );
        }
    }
};
</script>

<style scoped>
.documents-container {
    padding: 20px;
    width: 100%;
}

.page-header {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #1dbcbc;
}

.page-title {
    font-size: 2.5rem;
    font-weight: bold;
    color: #333;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 15px;
}

.documents-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eee;
}

.breadcrumb {
    font-size: 14px;
    color: #666;
}

.breadcrumb a {
    color: #1dbcbc;
    text-decoration: none;
}

.breadcrumb a:hover {
    text-decoration: underline;
}

.breadcrumb a.current {
    color: #333;
    pointer-events: none;
}

.toolbar {
    display: flex;
    gap: 10px;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
}

.btn-primary {
    background-color: #1dbcbc;
    color: white;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn:hover {
    opacity: 0.8;
}

.documents-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
}

.document-item {
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 15px;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
}

.document-item:hover {
    border-color: #1dbcbc;
    box-shadow: 0 2px 8px rgba(29, 188, 188, 0.1);
}

.document-item.folder {
    background-color: #f8f9fa;
    cursor: pointer;
}

.document-item.folder:hover {
    background-color: #e9ecef;
    transform: translateY(-2px);
}

.document-item.file {
    cursor: pointer;
}

.document-icon {
    font-size: 32px;
    text-align: center;
    margin-bottom: 10px;
}

.document-name span {
    font-weight: 500;
    display: block;
    margin-bottom: 5px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.3;
    max-width: 100%;
}

.document-name small {
    color: #666;
    font-size: 12px;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.document-actions {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 5px;
    opacity: 0;
    transition: opacity 0.2s;
    flex-wrap: wrap;
}

.back-button {
    border: 2px dashed #1dbcbc !important;
    background-color: #f0fffe !important;
}

.back-button:hover {
    border-color: #159999 !important;
    background-color: #e6fffd !important;
}

.document-item.selected {
    border-color: #1dbcbc;
    background-color: #f0fffe;
    box-shadow: 0 4px 12px rgba(29, 188, 188, 0.2);
}

.action-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    border-top: 2px solid #1dbcbc;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    z-index: 100;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
}

.action-bar-content {
    display: flex;
    align-items: center;
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
    gap: 20px;
    position: relative;
}

.selected-item-info {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-shrink: 0;
}

.selected-icon {
    font-size: 3rem;
}

.selected-details h4 {
    margin: 0;
    font-size: 1.2rem;
    color: #333;
    word-wrap: break-word;
    max-width: 300px;
}

.selected-details p {
    margin: 5px 0 0 0;
    color: #666;
    font-size: 0.9rem;
}

.action-buttons {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    flex-grow: 1;
    justify-content: center;
}

.action-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    padding: 15px 20px;
    border: 2px solid #ddd;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 80px;
}

.action-button:hover {
    border-color: #1dbcbc;
    background-color: #f0fffe;
}

.action-button.primary {
    border-color: #1dbcbc;
    background-color: #1dbcbc;
    color: white;
}

.action-button.primary:hover {
    background-color: #159999;
}

.action-button.secondary {
    border-color: #28a745;
    background-color: #28a745;
    color: white;
}

.action-button.secondary:hover {
    background-color: #218838;
    border-color: #1e7e34;
}

.action-button.danger {
    border-color: #dc3545;
}

.action-button.danger:hover {
    border-color: #c82333;
    background-color: #f8d7da;
}

.action-icon {
    font-size: 1.5rem;
}

.action-button span:last-child {
    font-size: 0.9rem;
    font-weight: 500;
}

.close-action-bar {
    position: absolute;
    top: 10px;
    right: 20px;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    padding: 5px;
}

.close-action-bar:hover {
    color: #333;
}

.document-item:hover .document-actions {
    opacity: 1;
}

.action-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    font-size: 12px;
}

.action-btn:hover {
    background-color: #f0f0f0;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal {
    background: white;
    border-radius: 8px;
    min-width: 600px;
    max-width: 95vw;
    max-height: 95vh;
    overflow: hidden;
    width: 80vw;
}

.modal-header {
    padding: 25px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.5rem;
    color: #333;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    padding: 5px;
}

.close-btn:hover {
    color: #333;
}

.modal-body {
    padding: 25px;
    min-height: 200px;
}

.modal-footer {
    padding: 20px 25px;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: flex-end;
    gap: 15px;
}

.upload-zone {
    border: 2px dashed #ddd;
    border-radius: 8px;
    padding: 60px 40px;
    text-align: center;
    margin-bottom: 20px;
    font-size: 16px;
}

.upload-zone:hover {
    border-color: #1dbcbc;
}

.upload-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #eee;
    border-radius: 6px;
    background: #f9f9f9;
}

.upload-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    border-bottom: 1px solid #eee;
    font-size: 14px;
}

.upload-item:last-child {
    border-bottom: none;
}

.form-control {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 16px;
}

.form-control:focus {
    border-color: #1dbcbc;
    outline: none;
}

.large-modal {
    min-width: 700px;
    max-width: 90vw;
}

.permissions-section {
    margin-bottom: 20px;
}

.form-group {
    margin-bottom: 20px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 16px;
}

.checkbox-label input[type="checkbox"] {
    margin: 0;
    transform: scale(1.2);
}

.users-section h4, .users-section h5 {
    margin: 20px 0 10px 0;
    color: #333;
}

.user-search {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    align-items: center;
}

.user-search select {
    flex: 1;
}

.current-users {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 15px;
}

.no-users {
    color: #666;
    font-style: italic;
    text-align: center;
    padding: 20px;
}

.user-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.user-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
}

.user-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.user-name {
    font-weight: 500;
    color: #333;
}

.user-email {
    font-size: 12px;
    color: #666;
}

.btn-danger {
    background-color: #dc3545;
    color: white;
}

.btn-danger:hover {
    background-color: #c82333;
}

.btn-sm {
    padding: 4px 8px;
    font-size: 12px;
}
</style>
